import i18n from '../i18n';

/**
 * Get the current locale for date formatting
 * Maps i18n language codes to locale strings
 */
export const getLocale = () => {
  const lang = i18n.language || 'en';
  return lang === 'ko' ? 'ko-KR' : 'en-US';
};

/**
 * Format a date using the current locale
 * @param {Date|string|number} date - The date to format
 * @param {Intl.DateTimeFormatOptions} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (date, options = {}) => {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString(getLocale(), options);
};

/**
 * Format a date with time using the current locale
 * @param {Date|string|number} date - The date to format
 * @param {Intl.DateTimeFormatOptions} options - Formatting options
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date, options = {}) => {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString(getLocale(), options);
};

/**
 * Format time only using the current locale
 * @param {Date|string|number} date - The date to format
 * @param {Intl.DateTimeFormatOptions} options - Formatting options
 * @returns {string} Formatted time string
 */
export const formatTime = (date, options = {}) => {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleTimeString(getLocale(), options);
};

/**
 * Common date format presets
 */
export const DATE_FORMATS = {
  // "January 15, 2025"
  long: { year: 'numeric', month: 'long', day: 'numeric' },
  // "Jan 15, 2025"
  medium: { year: 'numeric', month: 'short', day: 'numeric' },
  // "1/15/2025" or "2025. 1. 15."
  short: { year: 'numeric', month: 'numeric', day: 'numeric' },
  // "Monday, January 15"
  dayMonth: { weekday: 'long', month: 'long', day: 'numeric' },
  // "Mon, Jan 15"
  shortDayMonth: { weekday: 'short', month: 'short', day: 'numeric' },
  // "January 15"
  monthDay: { month: 'long', day: 'numeric' },
  // "Jan 15"
  shortMonthDay: { month: 'short', day: 'numeric' },
};

/**
 * Common time format presets
 */
export const TIME_FORMATS = {
  // "3:30 PM"
  short: { hour: 'numeric', minute: '2-digit' },
  // "3:30:00 PM"
  long: { hour: 'numeric', minute: '2-digit', second: '2-digit' },
  // "3:30 PM" in Eastern Time
  eastern: { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' },
};

/**
 * Format day names based on locale
 * For Korean: keeps Korean day names (월, 화, etc.)
 * For English: converts Korean to English day names (Mon, Tue, etc.)
 * @param {string} days - Day string (may contain Korean characters)
 * @returns {string} Formatted day string in current locale
 */
export const formatDays = (days) => {
  if (!days) return '';

  const lang = i18n.language || 'en';

  // If Korean locale, keep Korean day names as-is
  if (lang === 'ko') {
    return days;
  }

  // For English locale, convert Korean to English
  const dayMap = {
    '월': 'Mon',
    '화': 'Tue',
    '수': 'Wed',
    '목': 'Thu',
    '금': 'Fri',
    '토': 'Sat',
    '일': 'Sun',
    '월요일': 'Monday',
    '화요일': 'Tuesday',
    '수요일': 'Wednesday',
    '목요일': 'Thursday',
    '금요일': 'Friday',
    '토요일': 'Saturday',
    '일요일': 'Sunday',
  };

  let result = days;
  Object.entries(dayMap).forEach(([korean, english]) => {
    result = result.replace(new RegExp(korean, 'g'), english);
  });
  return result;
};

/**
 * Format time in Eastern timezone using current locale
 * @param {Date|string|number} date - The date/time to format
 * @returns {string} Formatted time string (e.g., "9:00 AM" or "오전 9:00")
 */
export const formatTimeET = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleTimeString(getLocale(), TIME_FORMATS.eastern);
};

/**
 * Format date for course display (handles date-only strings)
 * @param {string} dateString - Date string (e.g., "2025-01-15" or "2025-01-15T00:00:00.000Z")
 * @returns {string|null} Formatted date string or null if invalid
 */
export const formatCourseDate = (dateString) => {
  if (!dateString) return null;
  // Handle different date formats
  const dateOnly = dateString.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day); // month is 0-indexed
  return formatDate(date, DATE_FORMATS.long);
};

/**
 * Format semester label based on locale
 * Converts English semester labels to Korean format
 * @param {string} label - Semester label (e.g., "Spring 2025")
 * @returns {string} Formatted semester label (e.g., "2025 봄학기" in Korean)
 */
export const formatSemesterLabel = (label) => {
  if (!label) return '';

  const lang = i18n.language || 'en';

  // If English locale, return as-is
  if (lang !== 'ko') {
    return label;
  }

  // For Korean locale, convert "Season Year" to "Year 시즌학기"
  const seasonMap = {
    'Spring': '봄학기',
    'Summer': '여름학기',
    'Fall': '가을학기',
    'Winter': '겨울학기',
  };

  // Match pattern like "Spring 2025" or "Fall 2024"
  const match = label.match(/^(Spring|Summer|Fall|Winter)\s+(\d{4})$/i);
  if (match) {
    const season = match[1].charAt(0).toUpperCase() + match[1].slice(1).toLowerCase();
    const year = match[2];
    const koreanSeason = seasonMap[season];
    if (koreanSeason) {
      return `${year} ${koreanSeason}`;
    }
  }

  // If pattern doesn't match, return original
  return label;
};
